import React, { useState, useCallback, useMemo } from 'react';
import { checkCompanyIdAvailabilityCallable, requestCompanyAccountCallable } from '../services/firebase';

const debounce = (func, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), delay);
  };
};

const CompanyRequestModal = ({ isOpen, onClose, setNotification }) => {
  // States für alle neuen Felder
  const [companyName, setCompanyName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [street, setStreet] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('Deutschland');
  const [vatId, setVatId] = useState('');
  
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAvailable, setIsAvailable] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [validationError, setValidationError] = useState('');

  const checkAvailability = useCallback(async (id) => {
    const reservedNames = ['www', 'admin', 'mail', 'ftp', 'app', 'meetform'];
    if (reservedNames.includes(id)) {
        setValidationError('Dieser Name ist reserviert.');
        setIsAvailable(false);
        return;
    }
    if (!id || id.length < 3) {
      setValidationError('Muss mind. 3 Zeichen lang sein.');
      setIsAvailable(null);
      return;
    }
    setValidationError('');
    setIsChecking(true);
    try {
      const result = await checkCompanyIdAvailabilityCallable({ companyId: id });
      setIsAvailable(result.data.isAvailable);
    } catch (error) {
      console.error("Fehler bei der Verfügbarkeitsprüfung:", error);
      setIsAvailable(false);
    } finally {
      setIsChecking(false);
    }
  }, []);

  const debouncedCheck = useMemo(() => debounce(checkAvailability, 500), [checkAvailability]);

  const handleSubdomainChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSubdomain(value);
    debouncedCheck(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setNotification({ message: 'Die Passwörter stimmen nicht überein.', type: 'error' });
      return;
    }
    if (!isAvailable) {
        setNotification({ message: 'Die gewählte Subdomain ist nicht verfügbar oder ungültig.', type: 'error' });
        return;
    }
    setIsSubmitting(true);
    
    // Erstellt das komplette Datenpaket
    const registrationData = {
      companyName,
      subdomain,
      address: { street, zipCode, city, country },
      vatId,
      adminUser: { firstName, lastName, email, phone, password }
    };

    try {
      await requestCompanyAccountCallable(registrationData);
      setNotification({ message: 'Ihre Anfrage wurde gesendet! Wir werden uns in Kürze bei Ihnen melden.', type: 'success' });
      onClose();
    } catch (error) {
      setNotification({ message: `Fehler: ${error.message}`, type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500" title="Schließen">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
        <h2 className="text-3xl font-bold text-center mb-6">Firma registrieren</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold border-b pb-2 mb-4">Unternehmensdaten</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input type="text" placeholder="Firmenname (z.B. Musterfirma GmbH)" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"/>
                <div className="flex items-center">
                    <input type="text" value={subdomain} onChange={handleSubdomainChange} className="w-full p-3 border rounded-l-lg dark:bg-gray-700 dark:border-gray-600" placeholder="ihre-firma" required />
                    <span className="px-3 py-3 bg-gray-200 dark:bg-gray-600 border border-l-0 rounded-r-lg dark:border-gray-600">.meetform.de</span>
                </div>
                 <div className="text-xs h-4 md:col-span-2">
                    {isChecking && <p className="text-gray-500">Prüfe Verfügbarkeit...</p>}
                    {!isChecking && isAvailable === true && <p className="text-green-500">Diese Subdomain ist verfügbar!</p>}
                    {!isChecking && isAvailable === false && subdomain.length > 2 && <p className="text-red-500">{validationError || 'Diese Subdomain ist bereits vergeben.'}</p>}
                    {!isChecking && validationError && <p className="text-red-500">{validationError}</p>}
                </div>
                <input type="text" placeholder="Straße und Hausnummer" value={street} onChange={(e) => setStreet(e.target.value)} required className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600 md:col-span-2"/>
                <input type="text" placeholder="Postleitzahl" value={zipCode} onChange={(e) => setZipCode(e.target.value)} required className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"/>
                <input type="text" placeholder="Stadt" value={city} onChange={(e) => setCity(e.target.value)} required className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"/>
                <input type="text" placeholder="Land" value={country} onChange={(e) => setCountry(e.target.value)} required className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"/>
                <input type="text" placeholder="Umsatzsteuer-ID (Optional)" value={vatId} onChange={(e) => setVatId(e.target.value)} className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"/>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold border-b pb-2 mb-4 mt-4">Ihr Administrator-Account</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input type="text" placeholder="Vorname" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"/>
                <input type="text" placeholder="Nachname" value={lastName} onChange={(e) => setLastName(e.target.value)} required className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"/>
                <input type="email" placeholder="E-Mail-Adresse" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"/>
                <input type="tel" placeholder="Telefonnummer (Optional)" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"/>
                <input type="password" placeholder="Passwort" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"/>
                <input type="password" placeholder="Passwort bestätigen" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"/>
              </div>
            </div>
          <button type="submit" disabled={isSubmitting || !isAvailable} className={`w-full mt-6 px-6 py-3 text-white rounded-lg font-bold text-lg shadow-md transition-all ${isSubmitting || !isAvailable ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {isSubmitting ? 'Wird gesendet...' : 'Anfrage senden'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CompanyRequestModal;

